import { useEffect } from 'react'
import type { JSX } from 'react'

import {
  UpgradeDialog,
  Button,
  Text,
  List,
  Image,
  CheckIcon,
} from '@buffer-mono/popcorn'

import { useUser } from '../../../../common/context/User'
import { Error } from '../../../../common/components/Error'
import useStartTrial from '../../../../common/hooks/useStartTrial'
import { prefixWithProduct } from '../../../../common/utils/prefixWithProduct'
import { setCookie, DATES } from '../../../../common/utils/cookies'

import trackStartTrialModalViewed from '../../../../tracking/TrialPaths/trackStartTrialModalViewed'
import trackStartTrialModalMainCTAClicked from '../../../../tracking/TrialPaths/trackStartTrialModalMainCTAClicked'
import useDismissObject from '../../../../common/hooks/useDismissObject'

import { MODALS } from '../../../../common/types'
import type { TrialPathsTrackingPayload } from '../../../../tracking/TrialPaths/TrialPathsTrackingTypes'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

const trialCheckPoints = [
  'Collaborate with unlimited users',
  'Schedule up to 2,000 posts',
  'Track audience growth with Insights',
  'Engage with your audience directly',
  'Save & manage Hashtags easily',
  'Get unlimited tags',
]

const imageUrl =
  'https://static.buffer.com/publish/team-member-upgrade-banner-approvals.png'

export const StartTrialModal = (): JSX.Element => {
  const user = useUser()
  const { modalData, openModal, dismissModal: closeAction } = useModalManager()
  const { cta, ctaButton, ctaView, ctaLocation, upgradePathName } =
    modalData || {}
  const prefixedCta = prefixWithProduct(cta)

  const { startTrial, trial, error, processing } = useStartTrial({
    user,
    attribution: {
      cta: prefixedCta,
      upgradePathName: upgradePathName || '',
    },
  })

  const trackingPayload: TrialPathsTrackingPayload = {
    ...(cta ? { cta: prefixedCta } : {}),
    ctaButton,
    upgradePathName: upgradePathName || '',
    ctaView,
    ctaLocation,
  }

  // Mechanism to dismiss  from appearing to a customer who
  // has signed up for free and starts a trial
  const { onDismissObject } = useDismissObject({
    organizationId: user.currentOrganization?.id || '',
    dismissedObject: {
      id: 'trial_signup_welcome_modal',
    },
  })

  useEffect(() => {
    if (trial?.__typename === 'StartTrialResponse') {
      onDismissObject()
      openModal({
        key: MODALS.success,
        data: {
          startedTrial: true,
        },
      })
    }
  }, [trial])

  useEffect(() => {
    trackStartTrialModalViewed({ payload: trackingPayload, user })
  }, [])

  const handleOnDismissModal = (): void => {
    setCookie({
      key: 'startTrialPrompt',
      value: true,
      expires: DATES.inMonthsFromNow(12),
    })
    closeAction()
  }

  const handleStartTrial = (): void => {
    startTrial()
    trackStartTrialModalMainCTAClicked({ payload: trackingPayload, user })
  }

  return (
    <UpgradeDialog open={true} onOpenChange={handleOnDismissModal}>
      <UpgradeDialog.Content>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>
            Try Buffer Team plan, free for 14 days
          </UpgradeDialog.Title>
          <UpgradeDialog.Description>
            With Buffer Team plan, you can:
          </UpgradeDialog.Description>
          <List>
            {trialCheckPoints.map((detail: string) => (
              <List.Item key={detail} icon={CheckIcon}>
                <Text as="span">{detail}</Text>
              </List.Item>
            ))}
          </List>
          <Button
            variant="primary"
            size="large"
            disabled={processing}
            onClick={handleStartTrial}
          >
            {processing ? 'Processing ...' : 'Start Your Free 14-day Trial'}
          </Button>
          <Text as="p" color="subtle">
            No credit card required
          </Text>
          {error && <Error isInline={false} error={error} />}
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            alt="Start Trial"
            src={imageUrl}
            style={{ objectFit: 'contain', paddingRight: 'var(--space-lg)' }}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
